import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type PerfilCargoResumen = components['schemas']['PerfilCargoResumen']
export type Emparejamientos = components['schemas']['EmparejamientosRead']
export type EmparejamientoCargo = components['schemas']['EmparejamientoRead']
export type PerfilTipico = components['schemas']['PerfilTipicoRead']
export type CondicionCatalogo = components['schemas']['CondicionCatalogoRead']
export type DuplicadoCargo = components['schemas']['DuplicadoRead']
export type EmparejamientoAccion = components['schemas']['EmparejamientoAccion']

export const PERFILES_CARGO_PAGE_SIZE = 10

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/sgsst/perfiles-cargo`
}

export function usePerfilesCargo(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<PerfilCargoResumen>(
    ['perfiles-cargo', businessId],
    businessId ? base(businessId) : undefined,
    params,
    PERFILES_CARGO_PAGE_SIZE
  )
}

/** Se pide entera: los posibles duplicados no se contestan desde una página y el catálogo
 * alimenta el combobox «elegir otro perfil» (excepción R3). */
export function useEmparejamientos(businessId: string | undefined) {
  return useQuery({
    queryKey: ['perfiles-cargo', businessId, 'emparejamientos'],
    queryFn: async () => (await api.get<Emparejamientos>(`${base(businessId)}/emparejamientos`)).data,
    enabled: !!businessId,
  })
}

export const PROPUESTAS_PAGE_SIZE = 10

export function usePropuestas(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<EmparejamientoCargo>(
    ['perfiles-cargo', businessId, 'propuestas'],
    businessId ? `${base(businessId)}/propuestas` : undefined,
    params,
    PROPUESTAS_PAGE_SIZE
  )
}

export function useAplicarEmparejamiento(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {positionId: string; accion: EmparejamientoAccion; catalogoKey?: string}) =>
      (
        await api.post<PerfilCargoResumen>(`${base(businessId)}/emparejamientos/${input.positionId}`, {
          accion: input.accion,
          catalogo_key: input.catalogoKey ?? null,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]})
      track('emparejamiento_aplicado', {business_id: businessId, accion: variables.accion})
    },
  })
}

export type PerfilCargoDetalle = components['schemas']['PerfilCargoDetalle']
export type PerfilBloqueRead = components['schemas']['BloqueRead']
export type PerfilLineaRead = components['schemas']['LineaRead']
export type PerfilAlcance = components['schemas']['AlcancePerfilRead']
export type PerfilVersion = components['schemas']['PerfilCargoVersion']
export type PerfilConfirmacion = components['schemas']['ConfirmacionRead']
export type PerfilLineaWrite = components['schemas']['LineaWrite']

const PERFIL_VERSIONES_PAGE_SIZE = 10

function detalleKey(businessId: string | undefined, positionId: string | undefined) {
  return ['perfiles-cargo', businessId, 'detalle', positionId]
}

export function usePerfilCargo(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: detalleKey(businessId, positionId),
    queryFn: async () => (await api.get<PerfilCargoDetalle>(`${base(businessId)}/${positionId}`)).data,
    enabled: !!businessId && !!positionId,
  })
}

export function useVersionesPerfil(businessId: string | undefined, positionId: string | undefined, page = 1) {
  return useQuery({
    queryKey: [...detalleKey(businessId, positionId), 'versiones', page],
    queryFn: async () =>
      (
        await api.get<Page<PerfilVersion>>(`${base(businessId)}/${positionId}/versiones`, {
          params: {page, page_size: PERFIL_VERSIONES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!positionId,
  })
}

export function useGuardarBorrador(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      versionBase: string | null
      jornadaDeclarada: string | null
      sedeCabecera: string | null
      areaCabecera: string | null
      jefeEmployeeId: string | null
      lineas: PerfilLineaWrite[]
    }) =>
      (
        await api.put<PerfilCargoDetalle>(`${base(businessId)}/${positionId}/borrador`, {
          version_base: input.versionBase,
          jornada_declarada: input.jornadaDeclarada,
          sede_cabecera: input.sedeCabecera,
          area_cabecera: input.areaCabecera,
          jefe_employee_id: input.jefeEmployeeId,
          lineas: input.lineas,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(detalleKey(businessId, positionId), data)
      queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]})
      track('perfil_cargo_borrador_guardado', {business_id: businessId})
    },
  })
}

/** Mutación y no consulta: cada llamada emite un `alcance_token` nuevo con su propio reloj. */
export function useAlcancePerfil(businessId: string, positionId: string) {
  return useMutation({
    mutationFn: async () => (await api.post<PerfilAlcance>(`${base(businessId)}/${positionId}/alcance`, {})).data,
  })
}

export function useConfirmarPerfil(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {exigibleDesde: string; motivo: string | null; alcanceToken: string}) =>
      (
        await api.post<PerfilConfirmacion>(`${base(businessId)}/${positionId}/confirmar`, {
          exigible_desde: input.exigibleDesde,
          motivo: input.motivo,
          alcance_token: input.alcanceToken,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]})
      track('perfil_cargo_confirmado', {business_id: businessId, creo_version: data.creo_version})
    },
  })
}

export type PerfilSugerencias = components['schemas']['SugerenciasRead']
export type PerfilSugerencia = components['schemas']['PerfilSugerenciaRead']
export type PerfilSugerenciaAccion = components['schemas']['SugerenciaAccion']

function sugerenciasKey(businessId: string | undefined, positionId: string | undefined) {
  return [...detalleKey(businessId, positionId), 'sugerencias']
}

/** `disponible` en `false` es un perfil sin confirmar, no un error ni un permiso denegado. */
export function useSugerenciasPerfil(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: sugerenciasKey(businessId, positionId),
    queryFn: async () => (await api.get<PerfilSugerencias>(`${base(businessId)}/${positionId}/sugerencias`)).data,
    enabled: !!businessId && !!positionId,
  })
}

export const SUGERENCIAS_PAGE_SIZE = 10

function sugerenciasListaKey(businessId: string | undefined, positionId: string | undefined) {
  return [...sugerenciasKey(businessId, positionId), 'lista']
}

/** `habilitada` espera a la cabecera: ese GET es el que aprende y sincroniza las filas. */
export function useSugerenciasLista(
  businessId: string | undefined,
  positionId: string | undefined,
  decidida: boolean,
  page: number,
  habilitada: boolean
) {
  return usePagedList<PerfilSugerencia>(
    [...sugerenciasListaKey(businessId, positionId), decidida],
    habilitada && businessId && positionId ? `${base(businessId)}/${positionId}/sugerencias-lista` : undefined,
    {page, search: '', extra: {decidida: decidida ? 'true' : 'false'}},
    SUGERENCIAS_PAGE_SIZE
  )
}

export function useDecidirSugerencia(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {sugerenciaId: string; accion: PerfilSugerenciaAccion; motivo: string | null}) =>
      (
        await api.post<PerfilSugerencias>(`${base(businessId)}/${positionId}/sugerencias/${input.sugerenciaId}`, {
          accion: input.accion,
          motivo: input.motivo,
        })
      ).data,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(sugerenciasKey(businessId, positionId), data)
      // Solo la lista: invalidar la cabecera volvería a correr el aprendizaje entero.
      queryClient.invalidateQueries({queryKey: sugerenciasListaKey(businessId, positionId)})
      queryClient.invalidateQueries({queryKey: detalleKey(businessId, positionId)})
      queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]})
      track('perfil_sugerencia_decidida', {business_id: businessId, accion: variables.accion})
    },
  })
}

export type PerfilVersionNueva = components['schemas']['VersionNuevaRead']

/** La versión anterior sigue siendo la exigible hasta que la nueva se confirme. */
export function useAbrirVersionNueva(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<PerfilVersionNueva>(`${base(businessId)}/${positionId}/versiones`, {})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['perfiles-cargo', businessId]}),
  })
}
